import { Backdrop, Box, CircularProgress, Typography } from "@mui/material"
import React from "react"

interface HaufeLoaderProps {
  message?: string
  size?: "small" | "medium" | "large"
  fullScreen?: boolean
  showMessage?: boolean
}

interface HaufeOverlayProps {
  open: boolean
  message?: string
  onClose?: () => void
  compact?: boolean
}

export const HaufeLoader: React.FC<HaufeLoaderProps> = ({
  message = "Kommunikation mit Haufe-System...",
  size = "medium",
  fullScreen = false,
  showMessage = true,
}) => {
  const getSizes = () => {
    switch (size) {
      case "small":
        return { spinner: 24, logo: 80 }
      case "large":
        return { spinner: 60, logo: 120 }
      default:
        return { spinner: 40, logo: 100 }
    }
  }

  const sizes = getSizes()

  const LoaderContent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
      }}
    >
      <CircularProgress size={sizes.spinner} />

      {showMessage && (
        <Typography variant="body2" color="text.secondary" textAlign="center">
          {message}
        </Typography>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1,
        }}
      >
        <img
          src="/haufe_connect.png"
          alt="Haufe"
          style={{
            width: sizes.logo,
            height: "auto",
            opacity: 0.7,
          }}
        />
        <Typography
          variant="caption"
          color="text.disabled"
          sx={{ fontSize: "0.75rem" }}
        >
          Dies kann einen Moment dauern...
        </Typography>
      </Box>
    </Box>
  )

  if (fullScreen) {
    return (
      <Box
        position="fixed"
        top={0}
        left={0}
        right={0}
        bottom={0}
        display="flex"
        justifyContent="center"
        alignItems="center"
        bgcolor="rgba(0, 0, 0, 0.7)"
        zIndex={1300}
      >
        <Box
          sx={{
            backgroundColor: "background.paper",
            borderRadius: 2,
            p: 4,
            minWidth: 300,
            textAlign: "center",
          }}
        >
          {LoaderContent}
        </Box>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: fullScreen ? "100vh" : "auto",
        py: fullScreen ? 0 : 4,
      }}
    >
      {LoaderContent}
    </Box>
  )
}

// NEW: Dedicated overlay for Haufe operations with better visibility
export const HaufeOverlay: React.FC<HaufeOverlayProps> = ({
  open,
  message = "Verarbeitung mit Haufe-System läuft...",
  onClose,
  compact = false,
}) => {
  const overlayStyles = compact
    ? {
        minWidth: 280,
        p: 3,
        borderRadius: 2,
      }
    : {
        minWidth: 350,
        p: 4,
        borderRadius: 3,
      }

  const logoSize = compact ? 80 : 120
  const spinnerSize = compact ? 40 : 50

  return (
    <Backdrop
      sx={{
        color: "#fff",
        zIndex: (theme) => theme.zIndex.drawer + 1,
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
      open={open}
      onClick={onClose}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: compact ? 2 : 3,
          backgroundColor: "background.paper",
          textAlign: "center",
          boxShadow: 24,
          ...overlayStyles,
        }}
        onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside
      >
        <CircularProgress size={spinnerSize} sx={{ color: "primary.main" }} />

        <Typography
          variant={compact ? "subtitle1" : "h6"}
          color="text.primary"
          gutterBottom
        >
          {message}
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: compact ? 1 : 2,
          }}
        >
          <img
            src="/haufe_connect.png"
            alt="Haufe Connect"
            style={{
              width: logoSize,
              height: "auto",
              opacity: 0.9,
            }}
          />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontStyle: "italic",
              fontSize: compact ? "0.8rem" : "0.875rem",
            }}
          >
            Kommunikation mit Haufe-System läuft...
            {!compact && (
              <>
                <br />
                Dies kann einen Moment dauern.
              </>
            )}
          </Typography>
        </Box>
      </Box>
    </Backdrop>
  )
}

// Enhanced inline loader for buttons with better styling
export const HaufeLoaderInline: React.FC<{
  size?: number
  compact?: boolean
}> = ({ size = 20, compact = false }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: compact ? 0.5 : 1,
      minWidth: compact ? "auto" : size * 2.5,
    }}
  >
    <CircularProgress size={size} sx={{ color: "inherit" }} />
    <img
      src="/haufe_connect.png"
      alt="Haufe"
      style={{
        width: compact ? size * 0.8 : size + 4,
        height: "auto",
        opacity: 0.8,
      }}
    />
  </Box>
)

// Hook to check if we should show Haufe loader
export const useHaufeLoader = () => {
  // This can be extended later to connect to a context
  // For now, it's a placeholder for future state management
  return {
    isHaufeLoading: false,
    setHaufeLoading: (loading: boolean) => {},
  }
}
